import { useScrollSpy } from '../../hooks/useScrollSpy';
import { CEW_PRESS_RELEASE_PDF_URL, CEW_REPORT_PDF_URL } from '../../lib/cew-site';
import { cn } from '../../lib/utils';
import { PdfDownloadButton } from '../shared/PdfDownloadButton';

interface RailItem {
  readonly id: string;
  readonly num: '01' | '02' | '03' | '04';
  readonly numLabel: string;
  readonly title: string;
  /** Brand color — matches the prototype's per-section accents (lines 336–375). */
  readonly accent: 'blue' | 'gold' | 'green2' | 'rose';
}

const ITEMS: ReadonlyArray<RailItem> = [
  {
    id: 'how-it-works',
    num: '01',
    numLabel: '01 · How It Works',
    title: 'Connecting credentials to workforce demand',
    accent: 'blue',
  },
  {
    id: 'data-views',
    num: '02',
    numLabel: '02 · Data Views',
    title: 'Five ways to explore the data',
    accent: 'gold',
  },
  {
    id: 'who-its-for',
    num: '03',
    numLabel: '03 · Who Is This For?',
    title: 'Built for the people shaping workforce outcomes',
    accent: 'green2',
  },
  {
    id: 'methodology',
    num: '04',
    numLabel: '04 · Methodology',
    title: 'How we built the data',
    accent: 'rose',
  },
];

/**
 * Active-state class set per accent — collapses what used to be three
 * parallel `Record<accent, string>` maps into one keyed lookup so the four
 * accents stay in lockstep. Mirrors the four
 * `.rail-section[href="#…"]:hover, .rail-section[href="#…"].active`
 * blocks in `_archive/home.html` lines 336–375.
 *
 * Special case: the How-It-Works row (blue accent) turns its title navy
 * per prototype lines 344–345; the other 3 turn their title to text-1.
 */
interface AccentClasses {
  /** Left border + tinted bg for the row container. */
  readonly border: string;
  /** Number-label color (the "01 · How It Works" caption). */
  readonly numText: string;
  /** Title color (the long-form headline below the number). */
  readonly titleText: string;
}

const ACCENT_CLASSES: Record<RailItem['accent'], AccentClasses> = {
  // A11y (WCAG 1.4.3): numText sits on the accent's own light /0.0x tint, so it
  // uses the AA-safe dark variant of each accent (blue→blue-link, green2→green-dark,
  // gold→gold-dark) rather than the bright fill color. rose (#C6354C) already
  // clears 4.5:1 on its tint.
  blue: {
    border: 'border-l-blue   bg-blue/[0.07]',
    numText: 'text-blue-link',
    titleText: 'text-navy',
  },
  gold: {
    border: 'border-l-gold   bg-gold/[0.09]',
    numText: 'text-gold-dark',
    titleText: 'text-text-1',
  },
  green2: {
    border: 'border-l-green2 bg-green2/[0.08]',
    numText: 'text-green-dark',
    titleText: 'text-text-1',
  },
  rose: {
    border: 'border-l-rose   bg-rose/[0.08]',
    numText: 'text-rose',
    titleText: 'text-text-1',
  },
};

/**
 * Sticky left rail showing the four homepage content sections. Active item
 * is driven by useScrollSpy — whichever section the reader has scrolled to
 * gets the highlighted treatment. Mirrors `_archive/home.html`'s
 * `.content-rail` block.
 *
 * CR-035 (Frank, 2026-08-28, `sticky-nav-mockup.png`): two PDF download
 * pills sit under the card — "Download Full Report" (solid blue) above
 * "Download Press Release" (outlined) — inside the SAME sticky owner as the
 * card, so they pin and release together (two independent sticky siblings
 * would drift apart at the bottom of the page). They are siblings of the
 * "Page contents" `<nav>`, not children: they are not page sections. A
 * deliberate divergence from the prototype rail, which had no CTAs. Below
 * `lg` the rail is hidden and the mobile drawer carries both links.
 *
 * Column widths (designer-ship + code reviews, 2026-08-30): the 3/12 column
 * has 184px of content at 1024 and ~248px at 1280, so the pills are
 * card-width (`fit="fill"`, `wrap`) with `density="rail"` (glyph from 1440
 * only) — content-width pills (245/272px) painted over the main column at
 * 1024–1310 and poked past the card at 1280. At the `lg` band the wrapped
 * card + pills can be taller than a short viewport, so the sticky owner is
 * capped at `100vh - 132px` and scrolls internally there; from `xl` it fits
 * and the cap is lifted so the card's panel shadow is never clipped. No
 * `overscroll-contain` on the owner: it made the whole rail column a wheel
 * dead zone at 1024–1279 even where nothing overflowed (designer-ship
 * re-verify 2026-08-30, measured with real wheel input) — scroll chaining
 * to the page after the owner's own 66–226px is the desired behaviour.
 */
export function HomeRail() {
  const ids = ITEMS.map((i) => i.id);
  const active = useScrollSpy(ids);

  return (
    <div className="sticky top-[132px] z-20 hidden lg:block lg:max-h-[calc(100vh-132px)] lg:overflow-y-auto xl:max-h-none xl:overflow-visible">
      <nav aria-label="Page contents">
        <ol className="overflow-hidden rounded-lg border border-border bg-white shadow-panel">
          {ITEMS.map((item) => {
            const isActive = item.id === active;
            const accent = ACCENT_CLASSES[item.accent];
            return (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  // CR-036 review suggestion: the corrected active state must
                  // reach AT users too, not just sighted ones.
                  aria-current={isActive ? 'true' : undefined}
                  className={cn(
                    'block border-l-[3px] border-b border-border-light px-[18px] py-[14px] transition last:border-b-0',
                    isActive ? accent.border : 'border-l-transparent bg-white hover:bg-surface',
                  )}
                >
                  {/* CR-022: this grey all-caps section-index label was 10px —
                    the exact "all-caps tags in grey are 10px" category UIS
                    flagged. Raised to the 16px `text-meta` token (same as the
                    DataViews "View 0X" + footer column labels). The title
                    below goes to text-base so the label doesn't out-size it. */}
                  <span
                    className={cn(
                      'mb-1 block text-meta font-extrabold uppercase',
                      isActive ? accent.numText : 'text-text-4',
                    )}
                  >
                    {item.numLabel}
                  </span>
                  <span
                    className={cn(
                      'block text-base font-semibold leading-[1.4]',
                      isActive ? accent.titleText : 'text-text-2',
                    )}
                  >
                    {item.title}
                  </span>
                </a>
              </li>
            );
          })}
        </ol>
      </nav>
      <div className="mt-8 flex flex-col gap-5">
        <PdfDownloadButton
          href={CEW_REPORT_PDF_URL}
          label="Download Full Report"
          variant="primary"
          density="rail"
          fit="fill"
          wrap
        />
        <PdfDownloadButton
          href={CEW_PRESS_RELEASE_PDF_URL}
          label="Download Press Release"
          variant="secondary"
          density="rail"
          fit="fill"
          wrap
        />
      </div>
    </div>
  );
}
